import React, { useEffect, useRef, useState } from "react";
import { X, RotateCw, Check, Eye, Undo2, RefreshCw } from "lucide-react";
import { loadImage, rotatedCanvas, warpQuad } from "../lib/quadCrop";

const MAX_WORK = 2400; // working resolution (longest side) — keeps warp fast, quality high
const CORNER_LABELS = ["أعلى يسار", "أعلى يمين", "أسفل يمين", "أسفل يسار"];

const defaultPoints = (w, h) => {
  const ix = Math.round(w * 0.08), iy = Math.round(h * 0.08);
  return [[ix, iy], [w - ix, iy], [w - ix, h - iy], [ix, h - iy]];
};

const QuadCropperModal = ({ imageSrc, onCancel, onConfirm }) => {
  const [img, setImg] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [work, setWork] = useState(null); // {canvas, url}
  const [points, setPoints] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [box, setBox] = useState({ w: 0, h: 0 });
  const wrapRef = useRef(null);
  const svgRef = useRef(null);
  const drag = useRef(null);

  useEffect(() => { loadImage(imageSrc).then(setImg).catch(() => onCancel()); }, [imageSrc, onCancel]);

  useEffect(() => {
    if (!img) return;
    const c = rotatedCanvas(img, rotation, MAX_WORK);
    setWork({ canvas: c, url: c.toDataURL("image/jpeg", 0.92) });
    setPoints(defaultPoints(c.width, c.height));
    setPreview(null);
  }, [img, rotation]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setBox({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    return () => ro.disconnect();
  }, [work, preview]);

  if (!work || !points) {
    return (
      <div className="fixed inset-0 z-[110] bg-black/80 flex items-center justify-center text-white font-bold" dir="rtl" data-testid="quad-cropper-loading">
        جارٍ تحميل الصورة...
      </div>
    );
  }

  const PAD = 30; // room for corner handles at the image edges
  const W = work.canvas.width, Hh = work.canvas.height;
  const scale = box.w && box.h ? Math.min((box.w - 2 * PAD) / W, (box.h - 2 * PAD) / Hh) : 0;
  const dw = W * scale, dh = Hh * scale;
  const toDisp = ([x, y]) => [x * scale, y * scale];
  const disp = points.map(toDisp);

  const clamp = (x, y) => [Math.min(W, Math.max(0, x)), Math.min(Hh, Math.max(0, y))];

  const onDown = (e, kind, idx) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const r = svgRef.current.getBoundingClientRect();
    drag.current = { kind, idx, start: [e.clientX - r.left, e.clientY - r.top], base: points.map((p) => [...p]) };
  };
  const onMove = (e) => {
    if (!drag.current || !scale) return;
    const r = svgRef.current.getBoundingClientRect();
    const cx = (e.clientX - r.left) / scale, cy = (e.clientY - r.top) / scale;
    const { kind, idx, start, base } = drag.current;
    if (kind === "corner") {
      const next = base.map((p) => [...p]);
      next[idx] = clamp(cx, cy);
      setPoints(next);
    } else {
      const dx = cx - start[0] / scale, dy = cy - start[1] / scale;
      const a = idx, b = (idx + 1) % 4;
      const next = base.map((p) => [...p]);
      next[a] = clamp(base[a][0] + dx, base[a][1] + dy);
      next[b] = clamp(base[b][0] + dx, base[b][1] + dy);
      setPoints(next);
    }
  };
  const onUp = () => { drag.current = null; };

  const showPreview = () => {
    setBusy(true);
    setTimeout(() => {
      try { setPreview(warpQuad(work.canvas, points, 900).toDataURL("image/jpeg", 0.85)); }
      finally { setBusy(false); }
    }, 10);
  };
  const confirm = () => {
    setBusy(true);
    setTimeout(() => {
      try { onConfirm(warpQuad(work.canvas, points, 1600).toDataURL("image/jpeg", 0.92)); }
      finally { setBusy(false); }
    }, 10);
  };

  const mids = disp.map((p, i) => { const q = disp[(i + 1) % 4]; return [(p[0] + q[0]) / 2, (p[1] + q[1]) / 2]; });
  const poly = disp.map((p) => p.join(",")).join(" ");
  const btn = "h-11 px-3 rounded-lg border-2 border-[#09637E] text-[#09637E] font-bold text-sm flex items-center justify-center gap-1 hover:bg-[#EBF4F6] disabled:opacity-50";

  return (
    <div className="fixed inset-0 z-[110] bg-black/85 flex items-center justify-center p-2 md:p-4" dir="rtl">
      <div className="bg-white rounded-2xl w-full max-w-5xl h-[96vh] md:h-[92vh] overflow-hidden flex flex-col" data-testid="quad-cropper">
        <div className="p-3 border-b border-[#EBF4F6] flex items-center justify-between shrink-0">
          <div>
            <h3 className="font-extrabold text-[#333333] text-base md:text-lg">قصّ دقيق للصورة</h3>
            <p className="text-xs text-[#666666]">{preview ? "معاينة النتيجة النهائية" : "اسحب الزوايا الأربع أو منتصف الأضلاع لتحديد المنتج بدقة"}</p>
          </div>
          <button onClick={onCancel} className="w-11 h-11 rounded-lg hover:bg-[#EBF4F6] flex items-center justify-center" data-testid="quad-crop-close" aria-label="إغلاق"><X /></button>
        </div>

        <div ref={wrapRef} className="relative flex-1 min-h-0 bg-[#111] flex items-center justify-center overflow-hidden">
          {preview ? (
            <img src={preview} alt="" className="max-w-full max-h-full object-contain p-2" data-testid="quad-crop-preview" />
          ) : scale > 0 && (
            <div className="relative overflow-visible" style={{ width: dw, height: dh }}>
              <img src={work.url} alt="" className="absolute inset-0 w-full h-full select-none pointer-events-none" draggable={false} />
              <svg ref={svgRef} width={dw} height={dh} className="absolute inset-0 overflow-visible" style={{ touchAction: "none" }}
                onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} data-testid="quad-crop-svg">
                <path d={`M0,0 H${dw} V${dh} H0 Z M${poly} Z`} fill="rgba(0,0,0,0.55)" fillRule="evenodd" />
                <polygon points={poly} fill="none" stroke="#7AB2B2" strokeWidth="2" />
                <polygon points={poly} fill="none" stroke="#fff" strokeWidth="1" strokeDasharray="6 4" />
                {mids.map((m, i) => (
                  <g key={`m${i}`} onPointerDown={(e) => onDown(e, "edge", i)} className="cursor-move" data-testid={`quad-edge-${i}`}>
                    <circle cx={m[0]} cy={m[1]} r="22" fill="transparent" />
                    <rect x={m[0] - 9} y={m[1] - 9} width="18" height="18" rx="4" fill="#fff" stroke="#09637E" strokeWidth="2" />
                  </g>
                ))}
                {disp.map((p, i) => (
                  <g key={`c${i}`} onPointerDown={(e) => onDown(e, "corner", i)} className="cursor-grab" data-testid={`quad-corner-${i}`} aria-label={CORNER_LABELS[i]}>
                    <circle cx={p[0]} cy={p[1]} r="26" fill="rgba(9,99,126,0.25)" />
                    <circle cx={p[0]} cy={p[1]} r="13" fill="#09637E" stroke="#fff" strokeWidth="3" />
                  </g>
                ))}
              </svg>
            </div>
          )}
        </div>

        <div className="p-3 border-t border-[#EBF4F6] space-y-2 shrink-0">
          {preview ? (
            <div className="flex gap-2">
              <button onClick={() => setPreview(null)} className={`${btn} flex-1`} data-testid="quad-crop-back"><Undo2 size={16} /> رجوع للتعديل</button>
              <button onClick={confirm} disabled={busy} data-testid="quad-crop-confirm"
                className="flex-1 h-11 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-extrabold flex items-center justify-center gap-1 disabled:opacity-50">
                <Check size={18} /> {busy ? "..." : "تأكيد القصّ"}
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2">
                <button onClick={() => setRotation((r) => (r + 90) % 360)} className={btn} data-testid="quad-crop-rotate"><RotateCw size={16} /> تدوير</button>
                <button onClick={() => setPoints(defaultPoints(W, Hh))} className={btn} data-testid="quad-crop-reset"><RefreshCw size={16} /> إعادة تعيين</button>
                <button onClick={showPreview} disabled={busy} className={btn} data-testid="quad-crop-show-preview"><Eye size={16} /> {busy ? "..." : "معاينة"}</button>
              </div>
              <div className="flex gap-2">
                <button onClick={onCancel} className="flex-1 h-11 rounded-lg border-2 border-[#7AB2B2] text-[#666666] font-bold hover:bg-[#EBF4F6]" data-testid="quad-crop-cancel">إلغاء</button>
                <button onClick={confirm} disabled={busy} data-testid="quad-crop-confirm"
                  className="flex-1 h-11 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-extrabold flex items-center justify-center gap-1 disabled:opacity-50">
                  <Check size={18} /> {busy ? "..." : "تأكيد القصّ"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuadCropperModal;
